import React, { useEffect, useRef, useState } from 'react';
import { onOpenAssistant } from '../../lib/assistantBus';
import { personalInfo } from '../../data/portfolioData';
import './Assistant.css';

const MAX_MESSAGE_LENGTH = 1000;
const MAX_TURNS = 20;
const EMAIL = personalInfo.email;

const INITIAL_MESSAGE = {
  role: 'assistant',
  content:
    "I answer questions about Chirag's experience, projects, and skills, using only what is published on this site. Ask away."
};

const SUGGESTIONS = [
  'What did he ship at Dyspute.ai?',
  'How does Converge handle concurrent edits?',
  'How much iOS work has he done?'
];

export const Assistant = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([INITIAL_MESSAGE]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isOpen, isLoading]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && isOpen) setIsOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen]);

  const turns = messages.length - 1;
  const limitReached = turns >= MAX_TURNS;

  const send = async (text) => {
    const trimmed = (text ?? input).trim();
    if (!trimmed || isLoading || limitReached) return;

    const history = [...messages.slice(1), { role: 'user', content: trimmed }].map(
      ({ role, content }) => ({ role, content })
    );

    setMessages((prev) => [...prev, { role: 'user', content: trimmed }]);
    setInput('');
    setError(null);
    setIsLoading(true);

    try {
      const res = await fetch('/.netlify/functions/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: history })
      });

      // A missing function comes back as an HTML page, not JSON. Say so
      // plainly instead of surfacing a parse error.
      const type = res.headers.get('content-type') || '';
      if (!type.includes('application/json')) {
        throw new Error(
          res.status === 404
            ? 'The assistant is offline right now. Email works: ' + EMAIL
            : 'The assistant returned an unexpected response. Try again in a moment.'
        );
      }
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Something went wrong');

      setMessages((prev) => [...prev, { role: 'assistant', content: data.reply }]);
    } catch (err) {
      setError(err.message || 'Unable to reach the assistant right now.');
    } finally {
      setIsLoading(false);
    }
  };

  // Other sections can open the panel, optionally asking a question.
  const sendRef = useRef(send);
  sendRef.current = send;
  useEffect(
    () =>
      onOpenAssistant((question) => {
        setIsOpen(true);
        if (question) {
          sendRef.current(question);
        } else {
          window.requestAnimationFrame(() => inputRef.current?.focus());
        }
      }),
    []
  );

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const open = () => {
    setIsOpen(true);
    window.requestAnimationFrame(() => inputRef.current?.focus());
  };

  return (
    <div className="assistant">
      {isOpen && (
        <section className="assistant-panel" aria-label="Portfolio assistant">
          <header className="assistant-head">
            <h2 className="meta assistant-head-title">Ask about Chirag</h2>
            <button
              type="button"
              className="meta assistant-close"
              onClick={() => setIsOpen(false)}
            >
              Close
            </button>
          </header>

          <div className="assistant-log" ref={scrollRef}>
            {messages.map((msg, i) => (
              <article className={`assistant-turn assistant-turn-${msg.role}`} key={i}>
                <p className="meta assistant-speaker">
                  {msg.role === 'user' ? 'You' : 'Assistant'}
                </p>
                <p className="assistant-text">{msg.content}</p>
              </article>
            ))}

            {isLoading && (
              <article className="assistant-turn assistant-turn-assistant" aria-live="polite">
                <p className="meta assistant-speaker">Assistant</p>
                <div className="assistant-skeleton">
                  <div className="skeleton skeleton-line" style={{ width: '92%' }} />
                  <div className="skeleton skeleton-line" style={{ width: '78%' }} />
                  <div className="skeleton skeleton-line" style={{ width: '55%' }} />
                </div>
              </article>
            )}

            {messages.length === 1 && !isLoading && (
              <div className="assistant-suggestions">
                {SUGGESTIONS.map((s) => (
                  <button key={s} type="button" onClick={() => send(s)}>
                    {s}
                  </button>
                ))}
              </div>
            )}

            {error && <p className="assistant-error">{error}</p>}
            {limitReached && (
              <p className="assistant-error">
                Conversation limit reached. Refresh the page to start a new one.
              </p>
            )}
          </div>

          <div className="assistant-compose">
            <textarea
              ref={inputRef}
              className="assistant-input"
              placeholder="Type a question"
              value={input}
              maxLength={MAX_MESSAGE_LENGTH}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              disabled={isLoading || limitReached}
              rows={2}
              aria-label="Your question"
            />
            <button
              type="button"
              className="meta assistant-send"
              onClick={() => send()}
              disabled={isLoading || limitReached || !input.trim()}
            >
              Send
            </button>
          </div>
        </section>
      )}

      <button
        type="button"
        className="meta assistant-toggle"
        onClick={() => (isOpen ? setIsOpen(false) : open())}
        aria-expanded={isOpen}
      >
        {isOpen ? 'Hide assistant' : 'Ask the assistant'}
      </button>
    </div>
  );
};

export default Assistant;
